import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { formatFullDateTime } from "../../../libs/dateFormat";

import {
  ManagerReportItem,
  REPORT_STATUS,
  getKoreanStringFromReportStatusCode,
  getManagerReportList,
  getStringFromReportStatusCode,
} from "../../../services/mangerReportServices";

import useInfiniteScroll, {
  INFINITE_CODE,
  InfiniteScrollCallbackArgs,
} from "../../../hooks/useInfiniteScroll";
import useServiceGroupBuilding from "../../../hooks/useServiceGroupBuilding";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import StickyHeader from "../../../components/Layout/StickyHeader";
import CommonButton from "../../../components/Button";
import HorizontalRule from "../../../components/Rule/Horizontal";
import { PostListNoPostIcon } from "../../../components/Talk/PostList";
import VerticalRule from "../../../components/Rule/Vertical";
import CustomDatePicker from "../../../components/Input/DatePicker";
import CommonPopup from "../../../components/Popup";
import {
  HistoryReservationLocationIcon,
  HistoryReservationPersonIcon,
} from "../../../components/History/ReservationItem";

type Props = {};

export default function ManagerReport(props: Props) {
  const navigate = useNavigate();
  const PAGE_SIZE = 10;
  const SCROLL_LOAD_START_PAGE = 2;
  const [reportList, setReportList] = useState<ManagerReportItem[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [popup, setPopup] = useState<
    "need_date_range" | "need_start_date" | ""
  >("");

  const dateRangeOption = getDateRangeOption();
  const [selectedOption, setSelectedOption] = useState<DateRangeOption>(
    dateRangeOption[0]
  );

  const [statusOption, setStatusOption] = useState<SummaryButtonOption[]>([
    { status: "전체", value: "전체" },
    { status: "접수", value: REPORT_STATUS.REQUESTED.toString() },
    { status: "접수확인", value: REPORT_STATUS.PROCESSING.toString() },
    { status: "처리완료", value: REPORT_STATUS.COMPLETED.toString() },
    { status: "취소", value: REPORT_STATUS.CANCELED.toString() },
  ]);
  const [selectedStatusOption, setSelectedStatusOption] =
    useState<SummaryButtonOption>(statusOption[0]);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [selectedBuilding, buildingSelectComponent] = useServiceGroupBuilding({
    categoryCode: "VOC",
    useAllOption: true,
  });

  const loadReportList = async (args?: InfiniteScrollCallbackArgs) => {
    let itemLength = INFINITE_CODE.ERROR;

    let dateRange;

    if (selectedOption.title === "직접 입력") {
      if (!startDate && !endDate) {
        setReportList([]);
        setStatusOption((prev) =>
          prev.map((option) => ({ ...option, count: 0 }))
        );
        return itemLength;
      }
      dateRange = {
        createdAtStart: formatFullDateTime(startDate as Date),
        createdAtEnd: formatFullDateTime(endDate ?? new Date()),
      };
    } else {
      dateRange = {
        createdAtStart: formatFullDateTime(
          selectedOption.createdAtStart as Date
        ),
        createdAtEnd: formatFullDateTime(selectedOption.createdAtEnd as Date),
      };
    }

    setIsLoading(true);
    try {
      const res = await getManagerReportList({
        buildingId: selectedBuilding?.buildingId,
        status:
          selectedStatusOption.value !== statusOption[0].value
            ? selectedStatusOption.value
            : undefined,
        ...dateRange,
        ...selectedExtraOption?.params,
        ...args,
      });

      if (res.status === HttpStatusCode.Ok) {
        itemLength = res.data.items.length;
        if (args?.page === 1) {
          setReportList(res.data.items);
        } else {
          setReportList((prev) => [...prev, ...res.data.items]);
        }
        setStatusOption((prev) =>
          prev.map((option) => {
            const count = res.data.summary.find(
              (item) => item.status === option.value
            );
            if (count) {
              return {
                ...option,
                count: count.count,
              } as SummaryButtonOption;
            }
            return { ...option, count: 0 };
          })
        );
      }
    } catch (e: any) {
    } finally {
      setIsLoading(false);
    }
    return itemLength;
  };

  const [, { resetScroll }] = useInfiniteScroll({
    isLoading: isLoading || reportList.length < PAGE_SIZE,
    callback: loadReportList,
    page: SCROLL_LOAD_START_PAGE,
    pageSize: PAGE_SIZE,
    startPage: SCROLL_LOAD_START_PAGE,
  });

  const extraOption: {
    title: string;
    params: { isReservation?: boolean; isWithIn24?: boolean };
  }[] = [
    {
      title: "예약건만 보기",
      params: {
        isReservation: true,
      },
    },
    {
      title: "접수 24시간 이내",
      params: {
        isWithIn24: true,
      },
    },
    {
      title: "접수 24시간 경과",
      params: {
        isWithIn24: false,
      },
    },
  ];
  const [selectedExtraOption, setSelectedExtraOption] = useState<
    | {
        title: string;
        params: { isReservation?: boolean; isWithIn24?: boolean };
      }
    | undefined
  >(undefined);

  useEffect(() => {
    setStartDate(null);
    setEndDate(null);
  }, [selectedOption]);

  useEffect(() => {
    resetScroll();
    loadReportList({ page: SCROLL_LOAD_START_PAGE - 1, pageSize: PAGE_SIZE });
  }, [
    selectedOption,
    selectedExtraOption,
    selectedStatusOption,
    selectedBuilding?.buildingId,
  ]);

  return (
    <div>
      {loadingComponent}
      <div className="px-5">
        <StickyHeader
          title="불편신고 관리"
          onBackClick={() => navigate("/view")}
        />
      </div>

      <div className="p-5 flex flex-col gap-2.5 text-3xl font-light">
        <div className="flex items-center gap-2.5">
          {buildingSelectComponent}에
        </div>
        <div>
          {selectedOption.title !== "직접 입력" && `${selectedOption.title} `}
          <strong>
            {statusOption.find((item) => item.status === "전체")?.count ?? 0}건
          </strong>
          이 <strong>접수</strong> 됐어요
        </div>
      </div>

      <div className="px-5 pb-5">
        <ComboButtonGroup
          options={dateRangeOption}
          selectedOption={selectedOption}
          onClick={(option: ComboButtonOption) => {
            setSelectedOption(option);
            setSelectedStatusOption(statusOption[0]);
          }}
        />
      </div>

      {selectedOption.title === "직접 입력" && (
        <div className="flex px-5 items-center pb-5 gap-2.5">
          <CustomDatePicker
            date={startDate}
            maxDate={new Date()}
            onChange={(date) => setStartDate(date)}
          />
          <div>~</div>
          <CustomDatePicker
            date={endDate}
            maxDate={new Date()}
            onChange={(date) => {
              date?.setHours(23, 59, 59, 999);
              setEndDate(date);
            }}
          />
          <CommonButton
            variant="tertiary"
            className="p-2.5"
            onClick={() => {
              if (!startDate && !endDate) {
                setPopup("need_date_range");
                return;
              }

              if (!startDate) {
                setPopup("need_start_date");
                return;
              }

              resetScroll();
              loadReportList({
                page: SCROLL_LOAD_START_PAGE - 1,
                pageSize: PAGE_SIZE,
              });
            }}
          >
            <ManagerReportSearchIcon />
          </CommonButton>
        </div>
      )}

      <div className=" bg-brand-neutral-10 py-2.5 px-5 grid grid-cols-5 gap-x-1">
        {statusOption.map((option, index) => (
          <SummaryButton
            key={index}
            {...option}
            onClick={() => setSelectedStatusOption(option)}
            active={selectedStatusOption.status === option.status}
          />
        ))}
      </div>

      <div className="px-5 pt-[30px] flex flex-col gap-2.5">
        <div className=" text-lg font-medium">불편신고 목록</div>
        <div className="flex gap-2 text-sm font-medium flex-wrap">
          {extraOption.map((option) => (
            <div
              key={option.title}
              className={clsx([
                "flex gap-1 whitespace-nowrap",
                {
                  "text-brand-primary-magenta":
                    option.title === selectedExtraOption?.title,
                  "text-brand-neutral-40":
                    option.title !== selectedExtraOption?.title,
                },
              ])}
              onClick={() =>
                setSelectedExtraOption((prev) => {
                  if (prev?.title === option.title) {
                    return undefined;
                  } else {
                    return option;
                  }
                })
              }
            >
              {option.title === selectedExtraOption?.title ? (
                <ManagerReportActiveCheck />
              ) : (
                <ManagerReportInactiveCheck />
              )}
              {option.title}
            </div>
          ))}
        </div>
        <HorizontalRule className="bg-black" margin="0" />
      </div>

      <div className="pt-5 px-5">
        {reportList && reportList.length > 0 ? (
          reportList.map((item, index, items) => (
            <React.Fragment key={index}>
              <div
                className="flex gap-2.5 relative"
                onClick={() =>
                  navigate(
                    `/manager/voc-manager/detail/${item.centerIncommodityId}`
                  )
                }
              >
                <div className="h-full w-[24px] flex justify-center absolute z-0">
                  {items.length > 1 && (
                    <VerticalRule
                      className={clsx([
                        "!h-full !bg-[#d9d9d9]",
                        {
                          "!h-[calc(100%-24px)] ": index === 0,
                          "!h-[24px]": index === items.length - 1,
                        },
                      ])}
                      wrapperClassName={clsx([
                        {
                          "!items-end": index === 0,
                          "!items-start": index === items.length - 1,
                        },
                      ])}
                    />
                  )}
                </div>
                <div className="w-[24px] pt-[18px] z-10">
                  <ReportStatusRadioIcon
                    status={getStringFromReportStatusCode[Number(item.status)]}
                  />
                </div>
                <div className="flex flex-col gap-1 pt-[18px]">
                  <div className="text-base font-bold">
                    {getKoreanStringFromReportStatusCode[Number(item.status)]}
                  </div>
                  <div className=" text-xs text-[#9f9f9f]">
                    {item.createdAt.slice(5)}
                  </div>
                </div>
                <div className="p-[15px] border border-brand-neutral-10 rounded-[10px] flex flex-col gap-2.5 flex-1">
                  <div className="flex flex-col gap-1.5">
                    {(item.isReservation || item.isScheduled) && (
                      <div className="py-px px-2 rounded-full bg-brand-cleaning-badge text-xs font-semibold text-white w-fit">
                        {item.isReservation
                          ? "예약"
                          : item.isScheduled
                          ? "처리예정"
                          : ""}
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <div className="text-base font-medium">
                        {item.serviceName}
                      </div>
                      <ManagerReportRightArrow />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 text-xs text-brand-neutral-50">
                    <div className="flex gap-1">
                      <div>
                        <HistoryReservationLocationIcon />
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <div>
                          {item.buildingName} {item.floorName}
                        </div>
                        <div>{item.location}</div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <HistoryReservationPersonIcon />
                      <div>
                        {item.companyName} {item.name}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {index < items.length - 1 && (
                <div className="h-5 w-[24px] flex justify-center">
                  <VerticalRule className="!h-full !bg-[#d9d9d9]" />
                </div>
              )}
            </React.Fragment>
          ))
        ) : (
          <div className="flex flex-col  items-center justify-center w-full h-40 gap-3 text-brand-neutral-60">
            <PostListNoPostIcon />
            불편신고 결과가 없어요
          </div>
        )}
      </div>

      <CommonPopup isOpen={popup === "need_date_range"} padding={0}>
        <div className="p-5 pt-0 text-xl font-medium flex items-center justify-center h-[110px]">
          기간을 입력해주세요
        </div>
        <div className="w-full flex">
          <CommonButton
            className=" rounded-none flex-1 h-bottom-button"
            onClick={() => {
              setPopup("");
            }}
          >
            확인
          </CommonButton>
        </div>
      </CommonPopup>
      <CommonPopup isOpen={popup === "need_start_date"} padding={0}>
        <div className="p-5 pt-0 text-xl font-medium flex items-center justify-center h-[110px]">
          조회 시작일을 입력해주세요
        </div>
        <div className="w-full flex">
          <CommonButton
            className=" rounded-none flex-1 h-bottom-button"
            onClick={() => {
              setPopup("");
            }}
          >
            확인
          </CommonButton>
        </div>
      </CommonPopup>
    </div>
  );
}

export type ReportStatusRadioProps = {
  status: string;
};

export const ReportStatusRadioIcon = ({ status }: ReportStatusRadioProps) => {
  switch (status) {
    case "PROGRESSED":
      return (
        <svg
          width="24"
          height="25"
          viewBox="0 0 24 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          data-testid="progressed-badge"
        >
          <rect
            x="0.5"
            y="1"
            width="23"
            height="23"
            rx="11.5"
            fill="white"
            stroke="url(#paint0_linear_968_22269)"
          />
          <rect
            x="7"
            y="7.5"
            width="10"
            height="10"
            rx="5"
            fill="url(#paint1_linear_968_22269)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_968_22269"
              x1="24"
              y1="0.5"
              x2="-1.55188"
              y2="7.07639"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#E0004D" />
              <stop offset="1" stopColor="#EA27C2" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_968_22269"
              x1="17"
              y1="7.5"
              x2="6.35338"
              y2="10.2402"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#E0004D" />
              <stop offset="1" stopColor="#EA27C2" />
            </linearGradient>
          </defs>
        </svg>
      );
    case "COMPLETED":
      return (
        <svg
          width="24"
          height="25"
          viewBox="0 0 24 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          data-testid="completed-badge"
        >
          <rect
            y="0.5"
            width="24"
            height="24"
            rx="12"
            fill="url(#paint0_linear_968_22277)"
          />
          <path
            d="M7.70459 11.6151L11.3864 14.6832L16.2955 8.54688"
            stroke="white"
            strokeWidth="1.5"
          />
          <defs>
            <linearGradient
              id="paint0_linear_968_22277"
              x1="24"
              y1="0.5"
              x2="-1.55188"
              y2="7.07639"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#E0004D" />
              <stop offset="1" stopColor="#EA27C2" />
            </linearGradient>
          </defs>
        </svg>
      );
    case "CANCELED":
      return (
        <svg
          width="24"
          height="25"
          viewBox="0 0 24 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          data-testid="canceled-badge"
        >
          <rect
            x="0.5"
            y="1"
            width="23"
            height="23"
            rx="11.5"
            fill="white"
            stroke="#9F9F9F"
          />
          <rect x="7" y="7.5" width="10" height="10" rx="5" fill="#D9D9D9" />
        </svg>
      );
    case "ERRORED":
      return (
        <svg
          width="24"
          height="25"
          viewBox="0 0 24 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          data-testid="errored-badge"
        >
          <rect
            x="1.5"
            y="2"
            width="21"
            height="21"
            rx="10.5"
            fill="white"
            stroke="#E0004D"
            strokeWidth="3"
          />
        </svg>
      );
    case "DEFAULT":
    default:
      return (
        <svg
          width="24"
          height="25"
          viewBox="0 0 24 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          data-testid="default-badge"
        >
          <rect
            x="0.5"
            y="1"
            width="23"
            height="23"
            rx="11.5"
            fill="white"
            stroke="#DBDBDB"
          />
        </svg>
      );
  }
};

export type SummaryButtonOption = {
  status: string;
  count?: number;
  active?: boolean;
  onClick?: () => void;
  value?: string;
};

export const SummaryButton = ({
  status,
  count,
  active,
  onClick,
}: SummaryButtonOption) => (
  <div
    className={clsx([
      "flex-1 flex flex-col items-center justify-center rounded-[10px] border p-2.5 bg-white gap-0.5",
      { " text-black border-black": active },
      { "text-brand-neutral-50 border-brand-neutral-10": !active },
    ])}
    onClick={onClick}
  >
    <div className="text-2xl font-semibold">{count ?? 0}</div>
    <div className="text-xs text-center">{status}</div>
  </div>
);

export type ComboButtonOption = {
  title: string;
  createdAtStart?: Date;
  createdAtEnd?: Date;
};

export const ComboButtonGroup = ({ options, onClick, selectedOption }: any) => (
  <div className="flex flex-row">
    {options?.map((option: any) => (
      <CommonButton
        key={option.title}
        onClick={() => onClick(option)}
        className="rounded-none flex-1 text-sm px-[10px] py-[11px] whitespace-nowrap"
        variant={selectedOption?.title === option.title ? "primary" : "default"}
      >
        {option.title}
      </CommonButton>
    ))}
  </div>
);

export const ComboButton = ({ option, onClick }: any) => (
  <button
    className="flex-1 flex flex-col items-center justify-center rounded-md border border-gray-300"
    onClick={() => onClick(option)}
  >
    <div className="text-lg font-light">{option.title}</div>
    <div className="text-sm text-gray-400">
      {option.createdAtStart &&
        option.createdAtStart.toLocaleDateString() +
          " ~ " +
          option.createdAtEnd?.toLocaleDateString()}
    </div>
  </button>
);

export type DateRangeOption = {
  title: string;
  createdAtStart?: Date;
  createdAtEnd?: Date;
};

export const getDateRangeOption = (): DateRangeOption[] => {
  const today = new Date();

  const thisWeekStart = new Date(today);
  thisWeekStart.setDate(today.getDate() - today.getDay());
  thisWeekStart.setHours(0, 0, 0, 0); // 이번 주의 시작일로 설정 (일요일 시작 기준)
  const thisWeekEnd = new Date(thisWeekStart);
  thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
  thisWeekEnd.setHours(23, 59, 59, 999); // 이번 주의 마지막 날로 설정

  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1); // 이번 달의 시작일로 설정
  const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  thisMonthEnd.setHours(23, 59, 59, 999); // 이번 달의 마지막 날로 설정

  return [
    {
      title: "오늘",
      createdAtStart: new Date(today.setHours(0, 0, 0, 0)),
      createdAtEnd: new Date(today.setHours(23, 59, 59, 999)),
    },
    {
      title: "이번주",
      createdAtStart: thisWeekStart,
      createdAtEnd: thisWeekEnd,
    },
    {
      title: "이번달",
      createdAtStart: thisMonthStart,
      createdAtEnd: thisMonthEnd,
    },
    { title: "직접 입력" },
  ];
};

export const ManagerReportInactiveCheck = () => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.9834 7.9046L7.66521 10.9728L12.5743 4.83643"
      stroke="#9F9F9F"
      strokeWidth="1.5"
    />
  </svg>
);

export const ManagerReportActiveCheck = () => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.9834 7.9046L7.66521 10.9728L12.5743 4.83643"
      stroke="url(#paint0_linear_2107_9527)"
      strokeWidth="1.5"
    />
    <defs>
      <linearGradient
        id="paint0_linear_2107_9527"
        x1="12.5743"
        y1="4.83643"
        x2="3.94269"
        y2="7.94659"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E0004D" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
    </defs>
  </svg>
);

export const ManagerReportRightArrow = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.2">
      <path
        d="M9 18.5L15 12.5L9 6.5"
        stroke="black"
        strokeWidth="1.75"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const ManagerReportSearchIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.3">
      <path
        d="M21 21L15.8571 15.8571M10.7143 18.4286C6.4538 18.4286 3 14.9748 3 10.7143C3 6.4538 6.4538 3 10.7143 3C14.9748 3 18.4286 6.4538 18.4286 10.7143C18.4286 14.9748 14.9748 18.4286 10.7143 18.4286Z"
        stroke="#1D1929"
        strokeWidth="1.75"
      />
    </g>
  </svg>
);
